import { useCallback, useEffect, useRef, useState } from 'react';
import type { Prospect } from '@prototypical/types';
import { api } from '../../../lib/api';
import { friendlyError } from '../../../lib/friendlyError';
import { mergeProspects, readHarvestPages } from './savedHarvests';

export function useSavedHarvests() {
  const [saved, setSaved] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const cursor = useRef<string | undefined>();
  const generation = useRef(0);
  const load = useCallback(async (restart = false) => {
    controller.current?.abort();
    const id = ++generation.current;
    const current = new AbortController(); controller.current = current;
    if (restart) { setSaved([]); cursor.current = undefined; }
    setLoading(true); setComplete(false); setError('');
    try {
      await readHarvestPages(api.prospectHarvests, current.signal, page => {
        if (id !== generation.current) return;
        setSaved(previous => mergeProspects(previous, page.hits)); cursor.current = page.nextCursor ?? undefined;
      }, cursor.current);
      if (id === generation.current) setComplete(true);
    } catch (reason) {
      if (id === generation.current && !current.signal.aborted) setError(friendlyError(reason, 'Saved harvests could not finish loading. Retry to continue.'));
    } finally { if (id === generation.current) setLoading(false); }
  }, []);
  useEffect(() => { void load(true); return () => { ++generation.current; controller.current?.abort(); }; }, [load]);
  return { saved, setSaved, loading, complete, error, load, cancel: () => controller.current?.abort() };
}
